import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import {
  NAICS_CODE_MAPPINGS,
  NAICSCodeMapping,
} from "../../../utils/naicsCodeMapping";
import DeletableChip from "./DeletableChip";

interface VirtualizedNAICSDropdownProps {
  selectedCodes: string[];
  onCodesChange: (codes: string[]) => void;
  placeholder?: string;
}

// Custom option component for better performance
const NAICSOption = React.memo(
  ({ option, props }: { option: NAICSCodeMapping; props: any }) => (
    <Box
      component="li"
      {...props}
      sx={{ fontSize: "0.875rem", padding: "8px 16px" }}
    >
      <Box>
        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight: 500, color: "#1976d2" }}
        >
          {option.code}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          sx={{ ml: 1, color: "#ffffff" }}
        >
          {option.displayName.split(":")[1]?.trim() || option.displayName}
        </Typography>
      </Box>
    </Box>
  )
);

const VirtualizedNAICSDropdown: React.FC<VirtualizedNAICSDropdownProps> = ({
  selectedCodes,
  onCodesChange,
  placeholder = "Search NAICS codes...",
}) => {
  const [inputValue, setInputValue] = useState("");

  // Optimized filtering with smart ranking for better UX
  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) {
      return NAICS_CODE_MAPPINGS.slice(0, 100); // Show first 100 by default
    }

    const searchTerm = inputValue.toLowerCase();
    const searchCode = inputValue.trim();

    // Filter matching options
    const matches = NAICS_CODE_MAPPINGS.filter(
      (option) =>
        option.displayName.toLowerCase().includes(searchTerm) ||
        option.code.includes(searchCode)
    );

    // Smart ranking: prioritize exact starts, then code starts, then contains
    const ranked = matches.sort((a, b) => {
      const aCodeStartsWith = a.code.startsWith(searchCode);
      const bCodeStartsWith = b.code.startsWith(searchCode);
      const aDisplayStartsWith = a.displayName
        .toLowerCase()
        .startsWith(searchTerm);
      const bDisplayStartsWith = b.displayName
        .toLowerCase()
        .startsWith(searchTerm);

      // Priority 1: Code starts with query (highest priority)
      if (aCodeStartsWith && !bCodeStartsWith) return -1;
      if (!aCodeStartsWith && bCodeStartsWith) return 1;

      // Priority 2: Display name starts with query
      if (aDisplayStartsWith && !bDisplayStartsWith) return -1;
      if (!aDisplayStartsWith && bDisplayStartsWith) return 1;

      // Priority 3: Shorter codes first (more specific)
      if (aCodeStartsWith && bCodeStartsWith) {
        return a.code.length - b.code.length;
      }

      // Priority 4: Alphabetical by code
      return a.code.localeCompare(b.code);
    });

    return ranked.slice(0, 200); // Limit to 200 results for performance
  }, [inputValue]);

  const selectedOptions = useMemo(() => {
    return NAICS_CODE_MAPPINGS.filter((option) =>
      selectedCodes.includes(option.code)
    );
  }, [selectedCodes]);

  const handleChange = useCallback(
    (event: any, newValue: NAICSCodeMapping[]) => {
      const newCodes = newValue.map((option) => option.code);
      onCodesChange(newCodes);
    },
    [onCodesChange]
  );

  const handleInputChange = useCallback((event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  return (
    <Autocomplete
      multiple
      id="naics-codes-autocomplete"
      options={filteredOptions}
      value={selectedOptions}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={(option: NAICSCodeMapping) =>
        `${option.code}: ${option.displayName.split(":")[1]?.trim() || option.displayName}`
      }
      isOptionEqualToValue={(
        option: NAICSCodeMapping,
        value: NAICSCodeMapping
      ) => option.code === value.code}
      filterOptions={(options) => options} // We handle filtering manually for performance
      limitTags={3}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Industry Segment (NAICS)"
          placeholder={selectedOptions.length === 0 ? placeholder : ""}
          variant="outlined"
          size="small"
          InputLabelProps={{
            ...params.InputLabelProps,
            sx: {
              color: "#e0e0e0",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            },
            shrink: selectedOptions.length > 0 || inputValue.length > 0,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#1e1e1e",
              "& fieldset": {
                borderColor: "#555",
              },
              "&:hover fieldset": {
                borderColor: "#777",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1976d2",
              },
            },
            "& .MuiInputBase-input": {
              color: "#e0e0e0",
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#888888",
              opacity: 1,
            },
            "& .MuiFormHelperText-root": {
              color: "#888888",
              fontSize: "0.75rem",
            },
          }}
        />
      )}
      renderTags={(tagValue, getTagProps) => (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            paddingTop: "2px",
          }}
        >
          {tagValue.map((option, index) => {
            const tagProps = getTagProps({ index });
            const tooltipText =
              option.displayName.split(":")[1]?.trim() || option.displayName;
            return (
              <DeletableChip
                key={option.code}
                label={option.code}
                tooltip={tooltipText}
                onDelete={() => {
                  if (tagProps.onDelete) {
                    tagProps.onDelete({} as any);
                  }
                }}
              />
            );
          })}
        </Box>
      )}
      renderOption={(props, option: NAICSCodeMapping) => {
        const { key, ...otherProps } = props;
        return (
          <NAICSOption key={option.code} option={option} props={otherProps} />
        );
      }}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxHeight: "300px",
          "& .MuiAutocomplete-option": {
            padding: "8px 16px",
            '&[aria-selected="true"]': {
              backgroundColor: "#1976d2 !important",
            },
            "&.Mui-focused": {
              backgroundColor: "#333333 !important",
            },
          },
        },
        "& .MuiAutocomplete-popper": {
          "& .MuiPaper-root": {
            backgroundColor: "background.raised",
            border: "1px solid",
            borderColor: "divider",
          },
        },
      }}
    />
  );
};

export default VirtualizedNAICSDropdown;
